import { useSupportModeStore } from "@/store/useSupportModeStore";
import { SupportViewType } from "@/gql/graphql";
import { isCdhIdIamIdMigrationEnabled } from "@/store/dxpIamAuthHeader";
import type { SupportModeSession } from "./supportModeSession";

export const dealerAccountAuthHeaders = (
  cdhId: string
): Record<string, string> => ({
  "dxp-account-ids": cdhId,
});

const impersonationHeadersFromSupportModeSession = (
  session: SupportModeSession
): Record<string, string> =>
  session.viewType === SupportViewType.Role
    ? { "dxp-role": session.targetRoleId }
    : { "dxp-user-id": session.targetUserId };

export const authHeadersFromSupportModeSession = (
  session: SupportModeSession
): Record<string, string> => ({
  ...dealerAccountAuthHeaders(session.cdhId),
  ...impersonationHeadersFromSupportModeSession(session),
});

export const isActiveSupportModeForAccount = (
  session: SupportModeSession | null,
  accountId: string | undefined
): session is SupportModeSession =>
  session !== null && accountId !== undefined && session.cdhId === accountId;

export const isActiveSupportModeForRouteAccount = (
  session: SupportModeSession | null,
  routeAccountId: string | undefined
): session is SupportModeSession => {
  if (session === null || routeAccountId === undefined) {
    return false;
  }

  return isCdhIdIamIdMigrationEnabled()
    ? session.iamAccountId === routeAccountId
    : session.cdhId === routeAccountId;
};

/** Only the impersonation header; callers already send `dxp-account-ids`. */
export const getSupportModeImpersonationHeadersForAccount = (
  accountId?: string | null
): Record<string, string> => {
  if (accountId === null || accountId === undefined || accountId.length === 0) {
    return {};
  }

  const session = useSupportModeStore.getState().session;
  if (!isActiveSupportModeForAccount(session, accountId)) {
    return {};
  }

  return impersonationHeadersFromSupportModeSession(session);
};
